import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Plus, Edit, Trash2, MessageSquare, X, Eye, File as FileIcon, MapPin, Loader2,
} from 'lucide-react';
import { formatCompanyDate } from '../utils/companyDateFormat';
import { useNavigate } from 'react-router-dom';
import {
  messageApi, employeeApi, positionApi, scheduleApi,
  getProfileImageUrl, getUploadedFileUrl,
} from '../services/api';
import {
  StaffMessage, CreateMessageRequest, Employee, Position, Schedule, MessageConversation,
} from '../types';
import { useLocationContext } from '../components/Layout';
import InactiveLocationBanner from '../components/InactiveLocationBanner';
import { usePermissions } from '../hooks/usePermissions';
import BasketSelect from '../components/BasketSelect';
import AssignmentCircleLink from '../components/AssignmentCircleLink';
import MessageConversationPanel from '../components/MessageConversationPanel';
import {
  filterEmployeesByPositionAssignment,
  filterEmployeesByScheduleAssignment,
} from '../utils/taskAssignmentEmployees';
import {
  employeeBasketExtras,
  positionBasketExtras,
  scheduleBasketExtras,
} from '../utils/taskAssignmentForm';
import {
  buildAssignmentNameMaps,
  detectInactiveAssignees,
  hasAnyAssignmentIds,
  stripInactiveAssignees,
} from '../utils/taskInactiveAssignees';
import InactiveAssigneesDialog from '../components/InactiveAssigneesDialog';
import { fixUtf8Mojibake } from '../utils/fixUtf8Mojibake';

const MessageAdmin: React.FC = () => {
  const [messages, setMessages] = useState<StaffMessage[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [positions, setPositions] = useState<Position[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [participantCategory, setParticipantCategory] = useState<'employees' | 'positions' | 'schedules'>('employees');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formReadOnly, setFormReadOnly] = useState(false);
  const [editingMessage, setEditingMessage] = useState<StaffMessage | null>(null);
  const [conversations, setConversations] = useState<MessageConversation[]>([]);
  const [messageFormFiles, setMessageFormFiles] = useState<File[]>([]);
  const [assignmentEmployeesModal, setAssignmentEmployeesModal] = useState<{
    kind: 'position' | 'schedule';
    id: number;
    label: string;
    employees: Employee[];
    loading: boolean;
  } | null>(null);
  const [inactiveDialog, setInactiveDialog] = useState<{
    inactive: ReturnType<typeof detectInactiveAssignees>;
    resolve: (choice: 'remove' | 'ignore' | 'cancel') => void;
  } | null>(null);

  const [formData, setFormData] = useState<CreateMessageRequest>({
    title: '',
    description: '',
    locationId: null,
    assignedToEmployeeIds: [],
    assignedToPositionIds: [],
    assignedToScheduleIds: [],
  });

  const { debouncedSelectedLocationId, selectedLocationId, locations, isSelectedLocationInactive, selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const { loading: permissionsLoading, hasMessageManagement } = usePermissions();

  useEffect(() => {
    if (permissionsLoading) return;
    if (!hasMessageManagement) navigate('/', { replace: true });
  }, [permissionsLoading, hasMessageManagement, navigate]);

  useEffect(() => {
    const loadReference = async () => {
      try {
        const [empData, posData, schedData] = await Promise.all([
          employeeApi.getAll(),
          positionApi.getAll(),
          scheduleApi.getAll(),
        ]);
        setEmployees(empData);
        setPositions(posData);
        setSchedules(schedData);
      } catch (error) {
        console.error('Error loading reference data:', error);
      }
    };
    loadReference();
  }, []);

  useEffect(() => {
    const load = async () => {
      if (permissionsLoading || !hasMessageManagement) return;
      if (!debouncedSelectedLocationId) {
        setMessages([]);
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const data = await messageApi.getAll({ locationId: debouncedSelectedLocationId });
        setMessages(data);
      } catch (error) {
        console.error('Error loading messages:', error);
        alert('Failed to load messages.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [permissionsLoading, hasMessageManagement, debouncedSelectedLocationId]);

  const effectiveLocationId = selectedLocationId ?? editingMessage?.locationId ?? formData.locationId ?? null;

  const employeesFilteredByLocation = useMemo(() => {
    if (!effectiveLocationId) return employees;
    return employees.filter((e) => {
      const mainId = e.profile?.mainLocationId;
      const subIds = e.profile?.subLocationIds || [];
      return mainId === effectiveLocationId || subIds.includes(effectiveLocationId);
    });
  }, [employees, effectiveLocationId]);

  const schedulesFilteredByLocation = useMemo(() => {
    if (!effectiveLocationId) return schedules;
    return schedules.filter((s) => (s.locationIds || []).includes(effectiveLocationId));
  }, [schedules, effectiveLocationId]);

  const resetForm = () => {
    setEditingMessage(null);
    setFormReadOnly(false);
    setConversations([]);
    setMessageFormFiles([]);
    setFormData({
      title: '',
      description: '',
      locationId: selectedLocationId ?? null,
      assignedToEmployeeIds: [],
      assignedToPositionIds: [],
      assignedToScheduleIds: [],
    });
    setParticipantCategory('employees');
  };

  const openCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const openEdit = async (msg: StaffMessage, readOnly = false) => {
    setEditingMessage(msg);
    setFormReadOnly(readOnly);
    setFormData({
      title: msg.title,
      description: msg.description || '',
      locationId: msg.locationId ?? selectedLocationId ?? null,
      assignedToEmployeeIds: msg.assignedToEmployeeIds || msg.participants?.map((p) => p.id) || [],
      assignedToPositionIds: msg.assignedToPositionIds || [],
      assignedToScheduleIds: msg.assignedToScheduleIds || [],
    });
    setMessageFormFiles([]);
    setShowForm(true);
    try {
      const full = await messageApi.getById(msg.id);
      setConversations(full.conversations || []);
    } catch {
      setConversations([]);
    }
  };

  const promptInactiveAssigneesResolution = (inactive: ReturnType<typeof detectInactiveAssignees>) =>
    new Promise<'remove' | 'ignore' | 'cancel'>((resolve) => {
      setInactiveDialog({ inactive, resolve });
    });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving || isSelectedLocationInactive || formReadOnly) return;
    if (!formData.title.trim()) {
      alert('Title is required.');
      return;
    }
    if (!formData.locationId) {
      alert('Please select a location.');
      return;
    }
    const hasEmployees = (formData.assignedToEmployeeIds?.length || 0) > 0;
    const hasPositions = (formData.assignedToPositionIds?.length || 0) > 0;
    const hasSchedules = (formData.assignedToScheduleIds?.length || 0) > 0;
    if (!hasEmployees && !hasPositions && !hasSchedules) {
      alert('Please add at least one participant.');
      return;
    }

    const formAssignments = {
      employeeIds: formData.assignedToEmployeeIds ?? [],
      positionIds: formData.assignedToPositionIds ?? [],
      scheduleIds: formData.assignedToScheduleIds ?? [],
    };
    let assignments = { ...formAssignments };
    const inactive = detectInactiveAssignees(
      formAssignments,
      formAssignments,
      buildAssignmentNameMaps(editingMessage ? { assignedTo: editingMessage.participants, assignedToPositions: editingMessage.assignedToPositions, assignedToSchedules: editingMessage.assignedToSchedules } : null)
    );
    if (inactive.length > 0) {
      const choice = await promptInactiveAssigneesResolution(inactive);
      if (choice === 'cancel') return;
      if (choice === 'remove') {
        const stripped = stripInactiveAssignees(formAssignments, inactive);
        assignments = stripped;
        setFormData((prev) => ({
          ...prev,
          assignedToEmployeeIds: stripped.employeeIds,
          assignedToPositionIds: stripped.positionIds,
          assignedToScheduleIds: stripped.scheduleIds,
        }));
      }
    }
    if (!hasAnyAssignmentIds(assignments)) {
      alert('No active participants remain.');
      return;
    }

    try {
      setSaving(true);
      const fd = new FormData();
      fd.append('title', formData.title);
      if (formData.description) fd.append('description', formData.description);
      if (formData.locationId) fd.append('locationId', formData.locationId.toString());
      assignments.employeeIds.forEach((id) => fd.append('assignedToEmployeeIds', id.toString()));
      assignments.positionIds.forEach((id) => fd.append('assignedToPositionIds', id.toString()));
      assignments.scheduleIds.forEach((id) => fd.append('assignedToScheduleIds', id.toString()));
      messageFormFiles.forEach((file) => fd.append('files', file));

      if (editingMessage) {
        const updated = await messageApi.update(editingMessage.id, fd);
        setMessages((prev) => prev.map((m) => (m.id === updated.id ? { ...m, ...updated } : m)));
        setEditingMessage(updated);
      } else {
        const created = await messageApi.create(fd);
        setMessages((prev) => [created, ...prev]);
        setEditingMessage(created);
        setConversations([]);
      }
      setMessageFormFiles([]);
    } catch (error) {
      console.error('Error saving message:', error);
      alert('Failed to save message.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (msg: StaffMessage) => {
    if (!window.confirm(`Delete message "${msg.title}"?`)) return;
    try {
      await messageApi.delete(msg.id);
      setMessages((prev) => prev.filter((m) => m.id !== msg.id));
    } catch (error: any) {
      console.error('Error deleting message:', error);
      const apiMessage = error?.response?.data?.message || error?.response?.data?.error;
      alert(apiMessage ? `Failed to delete message: ${apiMessage}` : 'Failed to delete message.');
    }
  };

  const openAssignmentEmployeesModal = async (
    kind: 'position' | 'schedule',
    id: number,
    label: string,
    locationId?: number | null
  ) => {
    setAssignmentEmployeesModal({ kind, id, label, employees: [], loading: true });
    const filtered =
      kind === 'position'
        ? filterEmployeesByPositionAssignment(employees, id, locationId ?? undefined)
        : filterEmployeesByScheduleAssignment(employees, id, locationId ?? undefined);
    setAssignmentEmployeesModal({ kind, id, label, employees: filtered, loading: false });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const renderParticipants = (msg: StaffMessage) => (
    <div className="flex items-center gap-2 flex-wrap">
      {msg.participants?.map((employee) => {
        const empProfileUrl = getProfileImageUrl(
          employee.profileImageUrl ?? employees.find((e) => e.id === employee.id)?.profileImageUrl
        );
        const initials = `${employee.firstName?.[0] || ''}${employee.lastName?.[0] || ''}`.toUpperCase();
        return empProfileUrl ? (
          <img key={employee.id} src={empProfileUrl} alt="" className="w-8 h-8 rounded-full object-cover" title={`${employee.firstName} ${employee.lastName}`} />
        ) : (
          <div key={employee.id} className="w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-semibold" title={`${employee.firstName} ${employee.lastName}`}>
            {initials}
          </div>
        );
      })}
      {(msg.assignedToPositions?.length
        ? msg.assignedToPositions
        : (msg.assignedToPositionIds || []).map((pid) => ({ id: pid, title: positions.find((p) => p.id === pid)?.title ?? '' })).filter((p) => p.title)
      ).map((position) => (
        <AssignmentCircleLink
          key={`pos-${position.id}`}
          kind="position"
          label={position.title}
          size="md"
          onClick={() => openAssignmentEmployeesModal('position', position.id, position.title, msg.locationId)}
        />
      ))}
      {(msg.assignedToSchedules?.length
        ? msg.assignedToSchedules
        : (msg.assignedToScheduleIds || []).map((sid) => ({ id: sid, name: schedules.find((s) => s.id === sid)?.name ?? '' })).filter((s) => s.name)
      ).map((schedule) => (
        <AssignmentCircleLink
          key={`sched-${schedule.id}`}
          kind="schedule"
          label={schedule.name}
          size="md"
          onClick={() => openAssignmentEmployeesModal('schedule', schedule.id, schedule.name, msg.locationId)}
        />
      ))}
    </div>
  );

  const handleConversationsChange = useCallback((next: MessageConversation[]) => {
    setConversations(next);
    if (editingMessage) {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === editingMessage.id ? { ...m, conversationsCount: next.length } : m
        )
      );
    }
  }, [editingMessage]);

  if (permissionsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!debouncedSelectedLocationId) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center max-w-md">
          <MapPin className="w-8 h-8 text-blue-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Select a Location</h2>
          <p className="text-sm text-neutral-600">Select a location from the top navigation to access Message Admin.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-start mb-4">
        <div>
        <h1 className="text-2xl font-bold text-neutral-800">Message Audit</h1>
        <p className="text-sm text-neutral-500 mt-1">
          Admin view of all messages by location. Staff use <a href="/messages" className="text-blue-600 hover:underline">Messages</a> for day-to-day chat.
        </p>
        </div>
        {!isSelectedLocationInactive && (
          <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
            <Plus className="h-4 w-4" /> Add Message
          </button>
        )}
      </div>

      {isSelectedLocationInactive && selectedLocation && (
        <InactiveLocationBanner locationName={selectedLocation.name} />
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Title</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Created By</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Created On</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Participants</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Conversation</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200">
              {loading ? (
                <tr><td colSpan={6} className="px-6 py-8 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-blue-600" /></td></tr>
              ) : messages.length === 0 ? (
                <tr><td colSpan={6} className="px-6 py-8 text-center text-neutral-500">No messages found.</td></tr>
              ) : (
                messages.map((msg) => (
                  <tr key={msg.id} className="hover:bg-neutral-50">
                    <td className="px-6 py-4 text-sm font-medium text-neutral-900">{fixUtf8Mojibake(msg.title)}</td>
                    <td className="px-6 py-4 text-sm text-neutral-900 whitespace-nowrap">
                      {msg.createdBy?.firstName} {msg.createdBy?.lastName}
                    </td>
                    <td className="px-6 py-4 text-sm text-neutral-900 whitespace-nowrap">
                      {formatCompanyDate(msg.createdAt)}
                    </td>
                    <td className="px-6 py-4">{renderParticipants(msg)}</td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => openEdit(msg, true)}
                        className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50 relative"
                        title="View conversation"
                      >
                        <MessageSquare className="h-4 w-4" />
                        {(msg.conversationsCount ?? 0) > 0 && (
                          <span className="absolute -top-2 -right-2 bg-blue-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center border-2 border-white">
                            {msg.conversationsCount}
                          </span>
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button onClick={() => openEdit(msg, true)} className="text-neutral-600 hover:text-neutral-900 p-1" title="View"><Eye className="h-4 w-4" /></button>
                        <button onClick={() => openEdit(msg, false)} className="text-blue-600 hover:text-blue-900 p-1" title="Edit"><Edit className="h-4 w-4" /></button>
                        <button onClick={() => handleDelete(msg)} className="text-red-600 hover:text-red-900 p-1" title="Delete"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-neutral-800">
                  {formReadOnly ? 'View Message' : editingMessage ? 'Edit Message' : 'Create Message'}
                </h2>
                <button onClick={() => { setShowForm(false); resetForm(); }} className="text-neutral-400 hover:text-neutral-600">
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Title <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    value={fixUtf8Mojibake(formData.title)}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    readOnly={formReadOnly}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-neutral-50"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Description</label>
                  <textarea
                    value={fixUtf8Mojibake(formData.description)}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    readOnly={formReadOnly}
                    rows={3}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-neutral-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Location <span className="text-red-500">*</span></label>
                  <select
                    value={formData.locationId ?? ''}
                    onChange={(e) => setFormData({ ...formData, locationId: e.target.value ? Number(e.target.value) : null })}
                    disabled={formReadOnly}
                    className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                    required
                  >
                    <option value="">Select Location</option>
                    {locations.map((loc) => (
                      <option key={loc.id} value={loc.id}>{loc.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">Participants <span className="text-red-500">*</span></label>
                  {!formReadOnly && (
                    <div className="flex gap-2 mb-3 border-b border-neutral-200">
                      {(['employees', 'positions', 'schedules'] as const).map((cat) => (
                        <button
                          key={cat}
                          type="button"
                          onClick={() => setParticipantCategory(cat)}
                          className={`px-3 py-1 text-sm border-b-2 ${participantCategory === cat ? 'border-blue-600 text-blue-600' : 'border-transparent text-neutral-600'}`}
                        >
                          {cat === 'employees' ? 'Employees' : cat === 'positions' ? 'Positions' : 'Schedules'}
                          ({cat === 'employees' ? formData.assignedToEmployeeIds.length : cat === 'positions' ? (formData.assignedToPositionIds?.length || 0) : (formData.assignedToScheduleIds?.length || 0)})
                        </button>
                      ))}
                    </div>
                  )}
                  {!formReadOnly && participantCategory === 'employees' && (
                    <BasketSelect
                      label=""
                      availableItems={employeesFilteredByLocation.map((e) => ({
                        id: e.id,
                        label: `${e.firstName} ${e.lastName} (${e.email})`,
                      }))}
                      extraSelectedItems={employeeBasketExtras(editingMessage ? { assignedTo: editingMessage.participants } : {})}
                      selectedIds={formData.assignedToEmployeeIds}
                      onChange={(ids) => setFormData({ ...formData, assignedToEmployeeIds: ids as number[] })}
                      placeholder="Select employees..."
                    />
                  )}
                  {!formReadOnly && participantCategory === 'positions' && (
                    <BasketSelect
                      label=""
                      availableItems={positions.filter((p) => p.isActive).map((p) => ({ id: p.id, label: p.title }))}
                      extraSelectedItems={positionBasketExtras(editingMessage ?? {})}
                      selectedIds={formData.assignedToPositionIds || []}
                      onChange={(ids) => setFormData({ ...formData, assignedToPositionIds: ids as number[] })}
                      placeholder="Select positions..."
                    />
                  )}
                  {!formReadOnly && participantCategory === 'schedules' && (
                    <BasketSelect
                      label=""
                      availableItems={schedulesFilteredByLocation.filter((s) => s.isActive).map((s) => ({ id: s.id, label: s.name }))}
                      extraSelectedItems={scheduleBasketExtras(editingMessage ?? {})}
                      selectedIds={formData.assignedToScheduleIds || []}
                      onChange={(ids) => setFormData({ ...formData, assignedToScheduleIds: ids as number[] })}
                      placeholder="Select schedules..."
                    />
                  )}
                  {formReadOnly && editingMessage && renderParticipants(editingMessage)}
                </div>
                {!formReadOnly && (
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">File Attachments</label>
                    <input
                      type="file"
                      multiple
                      onChange={(e) => e.target.files && setMessageFormFiles(Array.from(e.target.files))}
                      className="w-full text-sm"
                    />
                    {editingMessage?.attachments && editingMessage.attachments.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {editingMessage.attachments.map((att) => (
                          <a key={att.id} href={getUploadedFileUrl(att.filePath)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-blue-600 hover:underline">
                            <FileIcon className="h-3 w-3" /> {att.fileName} ({formatFileSize(att.fileSize)})
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {!formReadOnly && (
                  <div className="flex justify-end gap-2 pt-2">
                    <button type="button" onClick={() => { setShowForm(false); resetForm(); }} className="px-4 py-2 border border-neutral-300 rounded-md hover:bg-neutral-50">Cancel</button>
                    <button type="submit" disabled={saving || isSelectedLocationInactive} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50">
                      {saving ? 'Saving...' : editingMessage ? 'Update Message' : 'Create Message'}
                    </button>
                  </div>
                )}
              </form>

              {editingMessage && (
                <MessageConversationPanel
                  messageId={editingMessage.id}
                  conversations={conversations}
                  employees={employees}
                  onConversationsChange={handleConversationsChange}
                />
              )}
              {!editingMessage && (
                <p className="text-sm text-neutral-500 mt-4 border-t pt-4">Save the message first to start the conversation.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {assignmentEmployeesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold">{assignmentEmployeesModal.label}</h3>
              <button onClick={() => setAssignmentEmployeesModal(null)}><X className="h-5 w-5" /></button>
            </div>
            {assignmentEmployeesModal.loading ? (
              <Loader2 className="h-6 w-6 animate-spin mx-auto" />
            ) : assignmentEmployeesModal.employees.length === 0 ? (
              <p className="text-sm text-neutral-500">No employees found.</p>
            ) : (
              <ul className="space-y-2 max-h-64 overflow-y-auto">
                {assignmentEmployeesModal.employees.map((e) => (
                  <li key={e.id} className="text-sm">{e.firstName} {e.lastName}</li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {inactiveDialog && (
        <InactiveAssigneesDialog
          inactive={inactiveDialog.inactive}
          onResolve={(choice) => {
            inactiveDialog.resolve(choice);
            setInactiveDialog(null);
          }}
        />
      )}
    </div>
  );
};

export default MessageAdmin;
